// ThePrideReservations.jsx
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar'; // Assuming Sidebar component file path
import Profile from '../components/Dashboard/profile'; // Assuming Profile component file path
import AddEditReservationModal from '../components/AddEditReservationModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import ViewReservationModal from '../components/ViewReservationModal';
import styles from '../components/ReservationTable.module.css'; // Import CSS module

const ReservationTable = () => {
  const [reservations, setReservations] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [page, setPage] = useState(1);
  const reservationsPerPage = 8;

  const [addEditModalOpen, setAddEditModalOpen] = useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [currentReservation, setCurrentReservation] = useState(null);
  const [reservationToDelete, setReservationToDelete] = useState(null);
  const [formData, setFormData] = useState({});

  const [statusCounts, setStatusCounts] = useState({});

  // Define allowed room types as per your request
  const ALLOWED_ROOM_TYPES = ['Twin Room', 'Standard Room', 'Executive Room', 'Family Room'];
  // Define allowed meal plans as per your provided image
  const ALLOWED_MEAL_PLANS = ['Continental Plan (inc. Breakfast)', 'European Plan (only stay)'];

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.error("Invalid date string provided to formatDateForInput:", dateString);
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  const fetchReservations = async () => {
    try {
      const response = await axios.get('https://admin.thepridehotel.in/api/hotel_bookings');
      const formattedData = response.data.map(item => ({
        id: item.id,
        name: item.name,
        phone: item.phone,
        checkIn: item.check_in,
        checkOut: item.check_out,
        guests: item.guests,
        roomType: item.room_type,
        mealPlan: item.meal_plan,
        specialRequest: item.special_request,
        status: item.status,
        submittedAt: item.submitted_at,
        assignedRoomNumber: item.assigned_room_number,
      }));
      setReservations(formattedData);

      // Recalculate status counts based on the fetched data
      const counts = formattedData.reduce((acc, reservation) => {
        acc[reservation.status] = (acc[reservation.status] || 0) + 1;
        return acc;
      }, { all: formattedData.length });
      setStatusCounts(counts);

    } catch (error) {
      console.error('Error fetching reservations:', error);
      alert('Failed to load reservations. Please ensure the backend server is running and the "hotel_bookings" table exists, and its API endpoint is correct.');
    }
  };

  const displayReservations = useMemo(() => {
    let filtered = reservations;

    if (filterStatus !== 'all') {
      filtered = filtered.filter(reservation => reservation.status === filterStatus);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (reservation) =>
          reservation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          reservation.phone.includes(searchTerm) ||
          reservation.roomType.toLowerCase().includes(searchTerm.toLowerCase()) ||
          reservation.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (reservation.assignedRoomNumber && reservation.assignedRoomNumber.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Sort entries by ID in descending order
    filtered.sort((a, b) => b.id - a.id);

    return filtered;
  }, [reservations, filterStatus, searchTerm]);

  const totalPages = Math.ceil(displayReservations.length / reservationsPerPage);
  const currentReservations = displayReservations.slice(
    (page - 1) * reservationsPerPage,
    page * reservationsPerPage
  );

  const getStatusClassName = (status) => {
    switch (status) {
      case 'Booked': return styles.statusBooked;
      case 'Checked In': return styles.statusCheckedIn;
      case 'Checked Out': return styles.statusCheckedOut;
      case 'Cancelled': return styles.statusCancelled;
      case 'Reserved': return styles.statusReserved; // Keep class in case old data still has it
      case 'New': return styles.statusNew;
      default: return '';
    }
  };

  const handleAddReservationClick = () => {
    setCurrentReservation(null);
    setFormData({
      name: '',
      phone: '',
      checkIn: '',
      checkOut: '',
      guests: 1,
      roomType: ALLOWED_ROOM_TYPES[0] || '', // Set default to first allowed room type
      mealPlan: ALLOWED_MEAL_PLANS[0] || 'None', // Set default to first allowed meal plan
      specialRequest: '',
      status: 'New', // Default status for new bookings
    });
    setAddEditModalOpen(true);
  };

  const handleEditClick = (reservation) => {
    setCurrentReservation(reservation);
    setFormData({
      name: reservation.name,
      phone: reservation.phone,
      checkIn: formatDateForInput(reservation.checkIn),
      checkOut: formatDateForInput(reservation.checkOut),
      guests: reservation.guests,
      roomType: reservation.roomType,
      mealPlan: reservation.mealPlan,
      specialRequest: reservation.specialRequest,
      status: reservation.status,
    });
    setAddEditModalOpen(true);
  };

  const handleViewClick = (reservation) => {
    setCurrentReservation(reservation);
    setViewModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const { name, phone, checkIn, checkOut, guests, roomType, mealPlan, specialRequest, status } = formData;
    if (!name || !phone || !checkIn || !checkOut || !guests || !roomType || !mealPlan || !status) {
      alert('Please fill in all required fields: Name, Phone, Check-in, Check-out, Guests, Room Type, Meal Plan, Status.');
      return;
    }
    if (isNaN(parseInt(guests)) || parseInt(guests) <= 0) {
      alert('Number of Guests must be a positive number.');
      return;
    }
    if (new Date(checkIn) >= new Date(checkOut)) {
      alert('Check-out Date and Time must be after Check-in Date and Time.');
      return;
    }

    // Validate roomType and mealPlan against allowed lists
    if (!ALLOWED_ROOM_TYPES.includes(roomType)) {
        alert(`Invalid Room Type selected. Please choose from: ${ALLOWED_ROOM_TYPES.join(', ')}.`);
        return;
    }
    if (!ALLOWED_MEAL_PLANS.includes(mealPlan)) {
        alert(`Invalid Meal Plan selected. Please choose from: ${ALLOWED_MEAL_PLANS.join(', ')}.`);
        return;
    }

    try {
      if (currentReservation) {
        await axios.put(`https://admin.thepridehotel.in/api/hotel_bookings/${currentReservation.id}`, {
          name: name,
          phone: phone,
          check_in: checkIn,
          check_out: checkOut,
          guests: parseInt(guests),
          room_type: roomType,
          meal_plan: mealPlan,
          special_request: specialRequest,
          status: status,
        });
        alert('Reservation updated successfully!');
      } else {
        await axios.post('https://admin.thepridehotel.in/api/hotel_bookings', {
          name: name,
          phone: phone,
          check_in: checkIn,
          check_out: checkOut,
          guests: parseInt(guests),
          room_type: roomType,
          meal_plan: mealPlan,
          special_request: specialRequest,
          status: status,
        });
        alert('Reservation added successfully!');
      }
      handleModalClose();
      fetchReservations(); // Re-fetch data to update table
    } catch (error) {
      console.error('Error saving reservation:', error);
      if (error.response && error.response.data && error.response.data.error) {
        alert(`Error: ${error.response.data.error}`);
      } else {
        alert('Failed to save reservation. Please check console for details.');
      }
    }
  };

  const handleDeleteClick = (id) => {
    setReservationToDelete(id);
    setDeleteConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`https://admin.thepridehotel.in/api/hotel_bookings/${reservationToDelete}`);
      alert('Reservation deleted successfully!');
      handleModalClose();
      fetchReservations(); // Re-fetch data to update table
    } catch (error) {
      console.error('Error deleting reservation:', error);
      alert('Failed to delete reservation. Please check console for details.');
    }
  };

  const handleModalClose = () => {
    setAddEditModalOpen(false);
    setDeleteConfirmModalOpen(false);
    setViewModalOpen(false);
    setCurrentReservation(null);
    setFormData({});
  };

  // Define the status filters to be displayed, excluding 'Reserved'
  const statusFilters = ['all', 'Booked', 'Checked In', 'Checked Out', 'Cancelled', 'New'];

  return (
    <div className={styles.appContainer}>
      <link
        href="https://fonts.googleapis.com/css2?family=Open+Sans&display=swap"
        rel="stylesheet"
      />
      <Sidebar />
      <main className={styles.mainContent}>
        <Profile />
        <div className={styles.tableContainer}>
          <h1 className={styles.headerTitle}>Hotel Bookings</h1>
          <div className={styles.topControls}>
            <div className={styles.filterGroup}>
              {statusFilters.map((status) => ( // Use the updated statusFilters array
                <button
                  key={status}
                  className={`${styles.filterButton} ${filterStatus === status ? styles.filterButtonActive : styles.filterButtonInactive}`}
                  onClick={() => {
                    setFilterStatus(status);
                    setPage(1);
                  }}
                >
                  {status === 'all' ? 'All Bookings' : status}{' '}
                  ({statusCounts[status] !== undefined ? statusCounts[status] : 0})
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Search bookings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.search}
            />
            <button
              className={styles.modalSaveBtn}
              onClick={handleAddReservationClick}
            >
              Add New Booking
            </button>
          </div>

          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.tableHeader}>ID</th>
                  <th className={styles.tableHeader}>Name</th>
                  <th className={styles.tableHeader}>Phone</th>
                  <th className={styles.tableHeader}>Check In</th>
                  <th className={styles.tableHeader}>Check Out</th>
                  <th className={styles.tableHeader}>Guests</th>
                  <th className={styles.tableHeader}>Room Type</th>
                  <th className={styles.tableHeader}>Meal Plan</th>
                  <th className={styles.tableHeader}>Special Request</th>
                  <th className={styles.tableHeader}>Status</th>
                  <th className={styles.tableHeader}>Submitted At</th>
                  <th className={styles.tableHeader}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentReservations.length > 0 ? (
                  currentReservations.map((row, i) => (
                    <tr
                      key={row.id}
                      className={styles.dataRow}
                    >
                      <td className={styles.tableCell}>#{row.id}</td>
                      <td className={styles.tableCell}>{row.name}</td>
                      <td className={styles.tableCell}>{row.phone}</td>
                      <td className={styles.tableCell}>{new Date(row.checkIn).toLocaleString()}</td>
                      <td className={styles.tableCell}>{new Date(row.checkOut).toLocaleString()}</td>
                      <td className={styles.tableCell}>{row.guests}</td>
                      <td className={styles.tableCell}>{row.roomType}</td>
                      <td className={styles.tableCell}>{row.mealPlan}</td>
                      <td className={styles.tableCell} title={row.specialRequest}>{row.specialRequest ? `${row.specialRequest.substring(0, 20)}...` : 'N/A'}</td>
                      <td className={styles.tableCell}>
                        <span className={`${styles.status} ${getStatusClassName(row.status)}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className={styles.tableCell}>{new Date(row.submittedAt).toLocaleString()}</td>
                      <td className={`${styles.tableCell} ${styles.actionsCell}`}>
                        <svg
                          className={styles.viewIcon}
                          onClick={() => handleViewClick(row)}
                          xmlns="http://www.w3.org/2000/svg"
                          width="20" height="20" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                        <svg
                          className={styles.ellipsisIcon}
                          onClick={() => handleEditClick(row)}
                          xmlns="http://www.w3.org/2000/svg"
                          width="20" height="20" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        >
                          <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                        </svg>
                        <svg
                          className={styles.deleteIcon}
                          onClick={() => handleDeleteClick(row.id)}
                          xmlns="http://www.w3.org/2000/svg"
                          width="20" height="20" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="12" className={styles.tableCell} style={{ textAlign: 'center' }}>
                      No reservations found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.pagination}>
            <button
              className={`${styles.paginationButton} ${page === 1 ? styles.paginationButtonDisabled : ''}`}
              onClick={() => setPage(p => Math.max(p - 1, 1))}
              disabled={page === 1}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Previous
            </button>
            <div className={styles.pages}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                <span
                  key={n}
                  className={`${styles.pageNumber} ${n === page ? styles.pageNumberActive : ''}`}
                  onClick={() => setPage(n)}
                >
                  {n}
                </span>
              ))}
            </div>
            <button
              className={`${styles.paginationButton} ${page === totalPages ? styles.paginationButtonDisabled : ''}`}
              onClick={() => setPage(p => Math.min(p + 1, totalPages))}
              disabled={page === totalPages}
            >
              Next
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
        </div>

        <AddEditReservationModal
          isOpen={addEditModalOpen}
          onClose={handleModalClose}
          currentReservation={currentReservation}
          formData={formData}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          // Pass down the allowed room types and meal plans to the modal
          availableRoomTypes={ALLOWED_ROOM_TYPES}
          availableMealPlans={ALLOWED_MEAL_PLANS}
        />

        <DeleteConfirmationModal
          isOpen={deleteConfirmModalOpen}
          onClose={handleModalClose}
          onConfirm={confirmDelete}
        />

        <ViewReservationModal
          isOpen={viewModalOpen}
          onClose={handleModalClose}
          reservation={currentReservation}
        />
      </main>
    </div>
  );
};

export default ReservationTable;